import Sugar from 'sugar';
import Papa from 'papaparse';
import { html, css, nothing, TemplateResult } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { ltnToastServiceContext } from '../../../../ns-services/contexts.js';

import { ButtressEntity } from '@buttress/crag';

import { LtnToastService } from '../../../../ltn-toast/ltn-toast-service.js';

import JobSchema from '../../../../models/job-schema.js';
import Invitation from '../../../../models/invitation.js';
import CommercialRelationship from '../../../../models/commercial-relationship.js';
import Person from '../../../../models/person.js';
import Company from '../../../../models/company.js';
import WorkflowSchema from '../../../../models/workflow-schema.js';

import { normaliseCompanyNumber } from '../../../../helpers/company.js';
import { callLambda } from '../../../../helpers/lambda.js';
import { getActionFromLambdaResults } from '../../../../helpers/lambda.js';

import { NSEditableTable, EditableTableColumn, EditableTableRow } from '../../../../ns-elements/ns-editable-table.js';
import '../../../../ns-elements/ns-editable-table.js';

import NSSpreadsheet from './base.js';
import { createLogger } from '../../../../ns-services/logger.js';
import '../../../../ns-elements/ns-job-layout.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/spreadsheet/onboarding-companies.json';
import { jobSchema } from '../../job-schema.js';

const logger = createLogger('ns-spreadsheet-onboarding-companies');

type RelationshipMode = 'per-company' | 'bundle-new' | 'bundle-existing';
type OutputRow = Invitation & { relationship?: Omit<CommercialRelationship, 'id'> };

const RELATIONSHIP_TYPE_OPTIONS = [
  { value: 'Supplier', label: 'Supplier' },
  { value: 'Partner', label: 'Partner' },
  { value: 'Subcontractor', label: 'Subcontractor' },
];

const MANUAL_TABLE_COLUMNS: EditableTableColumn[] = [
  { key: 'companyName', label: 'Company Name', type: 'text' },
  // Optional: the invitee's company search starts from it, as a request-demo invitation's does
  { key: 'companyNumber', label: 'Company Number', type: 'text' },
  { key: 'contactName', label: 'Contact Name', type: 'text' },
  { key: 'jobTitle', label: 'Job Title', type: 'text' },
  { key: 'email', label: 'Email Address', type: 'email' },
  { key: 'relationshipType', label: 'Relationship Type', type: 'select', options: RELATIONSHIP_TYPE_OPTIONS },
];

@customElement(NSSpreadsheetOnboardingCompanies.is)
export default class NSSpreadsheetOnboardingCompanies extends NSSpreadsheet {
  static get is() {
    return 'ns-spreadsheet-onboarding-companies';
  }

  @query('ns-editable-table')
  private __table?: NSEditableTable;

  @query('#import-input')
  private __importInput?: HTMLInputElement;

  @consume({ context: ltnToastServiceContext })
  private __toast?: LtnToastService;

  @state()
  private __relationshipMode: RelationshipMode = 'per-company';

  @state()
  private __newRelationshipDescription = '';

  @state()
  private __existingRelationships: CommercialRelationship[] = [];

  @state()
  private __selectedExistingRelationshipId = '';

  @state()
  private __relationshipModeError = '';

  @state()
  private __manualRows: EditableTableRow[] = [];

  @state()
  private __manualError = '';

  static get schema(): JobSchema {
    return jobSchema(definition, {
      outcomes: { cancel: { type: 'button', label: 'Cancel' }, complete: { type: 'button', label: 'Complete' } },
    });
  }

  static styles = [
    ...super.styles,
    css`
      .relationship-mode .field label {
        display: block;
        margin-bottom: var(--wa-space-2xs);

        font-size: 0.9em;
        font-weight: 600;
      }

      .relationship-mode .hint {
        margin: var(--wa-space-2xs) 0 0;
        font-size: 0.85em;
      }

      .relationship-mode select,
      .relationship-mode input[type='text'] {
        width: 100%;
        max-width: 420px;
        box-sizing: border-box;

        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;

        background: var(--wa-color-surface-default);
        color: var(--wa-color-text-normal);
        border: none;
        outline: none;

        padding: var(--wa-space-xs) var(--wa-space-m);
        border-radius: var(--wa-border-radius-pill);

        font-size: 0.9em;
        font-family: inherit;
      }

      .relationship-mode select {
        padding-right: var(--wa-space-xl);

        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23656565' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 16px center;
      }

      .relationship-mode select:focus,
      .relationship-mode input[type='text']:focus {
        outline: 2px solid var(--wa-color-focus);
        outline-offset: -2px;
      }

      /* An empty alert region stays in the panel so it announces, but shouldn't add a gap */
      .job-panel > [role='alert']:empty {
        margin-top: 0;
      }

      .companies-actions {
        gap: var(--wa-space-xs);
      }

      /* Six columns are too cramped at half the card's width, so the table scrolls sideways instead */
      .table-scroll {
        overflow-x: auto;
      }

      .table-scroll ns-editable-table {
        min-width: 48em;
      }

      #import-input {
        display: none;
      }
    `,
  ];

  async connectedCallback() {
    await super.connectedCallback();

    if (!this._db) throw new Error('Unable to find db service');

    if (!this._auth) throw new Error('Unable to find auth service');

    if (!this._directory) throw new Error('Unable to find NSWorkflowService');

    if (!this.__toast) throw new Error('Unable to find toast service');

    const company = this._directory.company;
    if (company) {
      const result = await this._db.query('comRelationship', {
        status: { $eq: 'ACTIVE' },
        'parties.companyId': { $eq: company.id },
      });
      const relationships = (result.results ?? []) as unknown as CommercialRelationship[];
      this.__existingRelationships = relationships.filter((relationship) =>
        relationship.parties.some((party) => party.companyId === company.id && party.partyType === 'CLIENT'),
      );
    }
  }

  private __onRelationshipModeChange(mode: RelationshipMode) {
    this.__relationshipMode = mode;
    this.__relationshipModeError = '';
  }

  private __onNewRelationshipDescriptionInput(evt: InputEvent) {
    const target = evt.target as HTMLInputElement;
    this.__newRelationshipDescription = target.value;
    this.__relationshipModeError = '';
  }

  private __onExistingRelationshipChange(evt: InputEvent) {
    const target = evt.target as HTMLSelectElement;
    this.__selectedExistingRelationshipId = target.value;
    this.__relationshipModeError = '';
  }

  private __onManualRowsChanged(evt: CustomEvent<{ rows: EditableTableRow[] }>) {
    this.__manualRows = evt.detail.rows;
    this.__manualError = '';
  }

  private __onImportClick() {
    this.__importInput?.click();
  }

  private __onImportFileChange(evt: Event) {
    const input = evt.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const rows = results.data
          .map((csvRow) => {
            const row: EditableTableRow = {};
            for (const column of MANUAL_TABLE_COLUMNS) {
              row[column.key] = csvRow[column.label] ?? '';
            }
            return row;
          })
          .filter((row) => Object.values(row).some((value) => value.trim()));

        this.__table?.appendRows(rows);
        this.__manualError = '';
      },
      error: (err) => {
        this.__manualError = 'Failed to read the file, please try again.';
        logger.error(err);
      },
    });
  }

  private __onExportClick() {
    const csv = Papa.unparse({
      fields: MANUAL_TABLE_COLUMNS.map((column) => column.label),
      data: this.__manualRows.map((row) => MANUAL_TABLE_COLUMNS.map((column) => row[column.key] ?? '')),
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'supply-chain-partners.csv';
    anchor.click();
    window.URL.revokeObjectURL(url);
  }

  private __renderModeSelector(): TemplateResult {
    return html`
      <div class="job-panel relationship-mode">
        <h3 id="relationship-mode-label">Relationship</h3>
        <p>Choose how the companies you're importing should be linked to yours.</p>

        <select
          id="relationship-mode"
          aria-labelledby="relationship-mode-label"
          @change=${(e: Event) =>
            this.__onRelationshipModeChange((e.target as HTMLSelectElement).value as RelationshipMode)}
        >
          <option value="per-company" ?selected=${this.__relationshipMode === 'per-company'}>
            Create a separate relationship per company
          </option>
          <option value="bundle-new" ?selected=${this.__relationshipMode === 'bundle-new'}>
            Bundle every imported company into one new relationship
          </option>
          <option value="bundle-existing" ?selected=${this.__relationshipMode === 'bundle-existing'}>
            Bundle every imported company into an existing relationship
          </option>
        </select>

        ${this.__relationshipMode === 'bundle-new'
          ? html`
              <div class="field">
                <label for="new-relationship-description">Relationship description</label>
                <input
                  id="new-relationship-description"
                  type="text"
                  .value=${this.__newRelationshipDescription}
                  @input=${this.__onNewRelationshipDescriptionInput}
                />
              </div>
            `
          : nothing}
        ${this.__relationshipMode === 'bundle-existing'
          ? html`
              <div class="field">
                <label for="existing-relationship">Existing relationship</label>
                <select id="existing-relationship" @change=${this.__onExistingRelationshipChange}>
                  <option value="" ?selected=${!this.__selectedExistingRelationshipId}>Select a relationship</option>
                  ${this.__existingRelationships.map(
                    (relationship) => html`
                      <option
                        value=${relationship.id}
                        ?selected=${relationship.id === this.__selectedExistingRelationshipId}
                      >
                        ${relationship.description}
                      </option>
                    `,
                  )}
                </select>
                ${this.__existingRelationships.length === 0
                  ? html`
                      <p class="hint">No existing relationships found where your company is the client.</p>
                    `
                  : nothing}
              </div>
            `
          : nothing}
        <div id="relationship-mode-error" class="error-text" role="alert">${this.__relationshipModeError}</div>
      </div>
    `;
  }

  private __validateRelationshipModeInputs(): boolean {
    if (this.__relationshipMode === 'bundle-new' && !this.__newRelationshipDescription.trim()) {
      this.__relationshipModeError = 'Please add a description for the new relationship.';
      return false;
    }
    if (this.__relationshipMode === 'bundle-existing' && !this.__selectedExistingRelationshipId) {
      this.__relationshipModeError = 'Please select an existing relationship.';
      return false;
    }
    return true;
  }

  render() {
    return html`
      <ns-job-layout>
        ${this._announcer.render()}
        <div class="job-panel">
          <h3>Invite a supply chain partners</h3>
          <p>
            Add the company details below and we'll send a secure invitation to join your workflow. They'll only be
            asked to provide what's needed to participate. The company number is optional; if you add it, they'll start
            from their Companies House record.
          </p>
        </div>
        ${this.__renderModeSelector()} ${this._renderMessages()}
        <div class="job-panel">
          <h3>Companies</h3>
          <div class="layout horizontal companies-actions">
            <wa-button variant="brand" type="button" @click=${this.__onExportClick}>Export</wa-button>
            <wa-button variant="brand" type="button" @click=${this.__onImportClick}>Import</wa-button>
            <input
              type="file"
              id="import-input"
              accept=".csv,text/csv,application/vnd.ms-excel"
              @change=${this.__onImportFileChange}
            />
          </div>
          <div id="manual-error" class="error-text" role="alert">${this.__manualError}</div>
          <div class="table-scroll" tabindex="0" role="region" aria-label="Companies table">
            <ns-editable-table
              .columns=${MANUAL_TABLE_COLUMNS}
              @rows-changed=${this.__onManualRowsChanged}
            ></ns-editable-table>
          </div>
        </div>
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <h3>Onboard your supply chain</h3>
          <p>
            Register organisations you work with in nodeStream to build a transparent, verifiable supply chain. This
            creates a shared record of relationships and enables clearer accountability before any work is assigned.
          </p>
          <h3>Connected partners reduce risk and delays</h3>
          <p>
            Onboarding supply chain companies creates visibility across your network, who you work with, how
            organisations are connected, and how value flows between them.
          </p>
          <p>
            Even without active tasks, this builds trust, reduces risk, and lays the groundwork for smoother
            collaboration when work begins.
          </p>
          <h3>What Happens Next</h3>
          <ul class="ticks">
            <li>Invite suppliers and partners</li>
            <li>Assign them to relevant workflows</li>
            <li>Control access through task-based permissions</li>
            <li>Build a verified record of collaboration</li>
          </ul>
        </div>
      </ns-job-layout>
    `;
  }

  private async __resolveUploadContext(): Promise<{
    profile: Person;
    company: Company;
    workflowSchema: WorkflowSchema;
  } | null> {
    if (!this._job) throw new Error('No job available');
    if (!this._workflowService) throw new Error('No workflow service available');

    if (!this._auth) throw new Error('Missing auth service');
    if (!this._db) throw new Error('Unable to find db service');
    if (!this._directory) throw new Error('No directory service available');
    if (!this._directory.me) throw new Error('Missing dir service person');

    const profile = this._directory.me;
    if (!profile) throw new Error('Unable to find user profile from directory service');
    if (!profile.email) throw new Error('Unable to find current users email to send invite.');

    const company = this._directory.company;
    if (!company) throw new Error('Unable to fetch active company from directory service');

    const workflowSchema = await this._workflowService.findWorkflowSchema('@ns/onboarding-supplier');
    if (!workflowSchema) throw new Error('Unable to find workflow schema, @ns/onboarding-supplier');

    const entry = workflowSchema.entry[NSSpreadsheetOnboardingCompanies.schema.name];
    if (!entry) throw new Error('Unable to find entry for workflow');

    return { profile, company, workflowSchema };
  }

  private async _outcomeCancelHandler() {
    this.outcome('cancel');
  }

  private async _outcomeCompleteHandler() {
    if (!this.__validateRelationshipModeInputs()) return;

    if (this.__manualRows.length === 0) {
      this.__manualError = 'Please add at least one company to invite.';
      return;
    }
    for (const row of this.__manualRows) {
      if (!row.companyName || !row.contactName || !row.jobTitle || !row.email) {
        this.__manualError = 'Please fill in company name, contact name, job title, and email for every row.';
        return;
      }
    }

    const context = await this.__resolveUploadContext();
    if (!context) return;
    const { profile, company, workflowSchema } = context;

    try {
      this.loading('Importing Data');

      const output: OutputRow[] = [];
      for (const row of this.__manualRows) {
        const builtRow = await this.__buildManualOutputRow(row, profile, company);
        if (builtRow) output.push(builtRow);
      }

      await this.__sendInvitationBatch(output, profile, company, workflowSchema);
    } catch (err) {
      this.loaded();
      logger.error('ooops', err);
    }
  }

  private async __buildManualOutputRow(
    row: EditableTableRow,
    profile: Person,
    company: Company,
  ): Promise<OutputRow | null> {
    if (!this._db) throw new Error('Unable to find db service');
    if (!profile.email) throw new Error('Unable to find current users email to send invite.');

    const invitation = this._db.createObject<Invitation>('invitation');
    invitation.inviter.personId = profile.id;
    invitation.inviter.email = profile.email;
    invitation.inviter.companyId = company.id;

    invitation.invitee.name = row.contactName;
    invitation.invitee.email = row.email;
    invitation.invitee.company.name = row.companyName;
    invitation.invitee.company.registrarIdentifier = normaliseCompanyNumber(row.companyNumber);
    invitation.invitee.jobTitle = row.jobTitle;
    invitation.expiryDate = Sugar.Date.create('in 1 day');

    const existingCount = await this._db.count('invitation', {
      'invitee.email': row.email,
      'invitee.company.name': row.companyName,
    });

    if (existingCount > 0) {
      this.addMessage(
        'warning',
        `Skipping invitation for ${row.contactName} from ${row.companyName} as it already exists`,
      );
      return null;
    }

    if (this.__relationshipMode !== 'per-company') {
      return { ...invitation };
    }

    // This job is always "onboard companies into my supply chain" — the base company is always
    // the CLIENT, every imported company is always the SUPPLIER. Relationship Type here is a
    // descriptive sub-category folded into the description, not a direction toggle.
    const relationshipType = row.relationshipType || 'Supplier';
    return {
      ...invitation,
      relationship: {
        description: `${company.name} <-> ${row.companyName} (${relationshipType})`,
        status: 'ACTIVE',
        parties: [{ companyId: company.id, partyType: 'CLIENT', status: 'ACCEPTED' }],
      },
    };
  }

  private async __sendInvitationBatch(
    output: OutputRow[],
    profile: Person,
    company: Company,
    workflowSchema: WorkflowSchema,
  ) {
    if (!this._job) throw new Error('No job available');
    if (!this._workflowService) throw new Error('No workflow service available');
    if (!this._auth) throw new Error('Missing auth service');

    this.loading('Creating invitations and emails');
    // Chunking the data to a 100 per a lambda call
    const invitations = this.__splitArrayIntoChunks(output);
    let bundleRelationshipId =
      this.__relationshipMode === 'bundle-existing' ? this.__selectedExistingRelationshipId : undefined;

    for await (const invitation of invitations) {
      const lambdaOutput = await callLambda(
        'onboarding/create-invitation',
        'POST',
        this._auth.token,
        JSON.stringify({
          userId: profile.userId,
          personId: profile.id,
          oauthApp: sessionStorage.getItem('oauthApp'),
          companyId: company.id,
          workflowParentId: this._job.workflow.id,
          invitations: invitation,
          relationshipMode: this.__relationshipMode === 'per-company' ? 'per-company' : 'bundle',
          relationshipId: bundleRelationshipId,
          newRelationshipDescription:
            this.__relationshipMode === 'bundle-new' && !bundleRelationshipId
              ? this.__newRelationshipDescription
              : undefined,
        }),
      );
      const res = lambdaOutput.res as unknown as { invitations: ButtressEntity[]; relationshipId?: string };
      if (this.__relationshipMode !== 'per-company' && res.relationshipId) {
        bundleRelationshipId = res.relationshipId;
      }

      for await (const inv of res.invitations) {
        const results = await this._workflowService.createWorkflowFromSchema(
          workflowSchema,
          '@ns/spreadsheet-onboarding-companies',
          {
            inviter: profile.id,
            BASE_COMPANY_ID: company.id,
            INVITATION_ID: inv.id,
          },
          {
            name: `${inv.invitee.company.name} onboarding as a supplier for ${company.name}`,
            parentId: this._job.workflow.id,
          },
        );

        // Make sure the lambda returned that the workflow was created.
        getActionFromLambdaResults(results, 'workflow_created');
      }
    }

    this.loaded();
    this.__toast?.queueToast({
      label: `${output.length} invitations generated and currently sending emails out.`,
      timeout: 4000,
    });
    this.outcome('complete');
  }

  private __splitArrayIntoChunks(arr: Invitation[], chunkSize: number = 100) {
    return Array.from({ length: Math.ceil(arr.length / chunkSize) }, (v, i) =>
      arr.slice(i * chunkSize, i * chunkSize + chunkSize),
    );
  }
}
